/**
 * Authenticated SSE utility using fetch + ReadableStream.
 * Sends JWT via Authorization header instead of query string (OWASP compliant).
 */

export interface SSESubscription {
  close: () => void;
}

interface FetchSSEOptions {
  onMessage?: (data: string, eventType: string) => void;
  onError?: (error: Error) => void;
  onOpen?: () => void;
  /** O servidor encerrou o stream sem erro — quem precisa manter a conexão reconecta. */
  onClose?: () => void;
  /** Cabeçalhos a mais, como a organização ativa (`X-Organization-Id`). */
  headers?: Record<string, string>;
}

function getAccessToken(): string | null {
  try {
    const getCookie = (name: string): string | null => {
      if (typeof document === 'undefined') return null;
      const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    };

    return (
      localStorage.getItem('access_token') ??
      localStorage.getItem('accessToken') ??
      getCookie('access_token')
    );
  } catch {
    return null;
  }
}

/**
 * Creates an authenticated SSE connection using fetch + ReadableStream.
 * Unlike EventSource, this allows sending JWT in the Authorization header.
 */
export function createAuthenticatedSSE(
  url: string,
  options: FetchSSEOptions
): SSESubscription {
  const controller = new AbortController();

  const token = getAccessToken();

  const headers: Record<string, string> = {
    'Accept': 'text/event-stream',
    ...(options.headers ?? {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const separator = url.includes('?') ? '&' : '?';
  const finalUrl = `${url}${separator}_t=${Date.now()}`;

  fetch(finalUrl, {
    headers,
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }
      if (!response.body) {
        throw new Error('No response body');
      }

      options.onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          options.onClose?.();
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!part.trim()) continue;

          let eventType = 'message';
          let data = '';

          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              data += (data ? '\n' : '') + line.slice(6);
            } else if (line.startsWith('data:')) {
              data += (data ? '\n' : '') + line.slice(5);
            }
          }

          if (data) {
            options.onMessage?.(data, eventType);
          }
        }
      }
    })
    .catch((error: Error) => {
      if (error.name !== 'AbortError') {
        options.onError?.(error);
      }
    });

  return {
    close: () => controller.abort(),
  };
}
